import {
  DefaultSkillMapTarget,
  AssessmentStatus,
  AssessmentQuestionType,
  AssessmentTagSource,
  OverallScoreLogicType,
} from '../../../_generated/graphql'

export interface AssessmentTag {
  tagMasterId: string
  name: string
  source: AssessmentTagSource
}

export interface AssessmentTagMaster {
  id: string
  name: string
}

interface FetchedAssessmentTag {
  tagMasterId: string
  name: string
  source: AssessmentTagSource
}

export const mappingAssessmentTags = (
  fetchedTags: FetchedAssessmentTag[],
): AssessmentTag[] => {
  return fetchedTags.map((tag) => ({
    tagMasterId: tag.tagMasterId,
    name: tag.name,
    source: tag.source,
  }))
}

export const mappingAssessment = (
  fetchedAssessment: FetchedAssessment,
): Assessment => {
  return {
    id: fetchedAssessment.id,
    orgId: fetchedAssessment.orgId,
    user: {
      id: fetchedAssessment.user.id,
      orgId: fetchedAssessment.user.orgId,
      name: fetchedAssessment.user.name,
      email: fetchedAssessment.user.email,
      departmentName: fetchedAssessment.user.departmentName,
    },
    businessMeeting: {
      id: fetchedAssessment.businessMeeting.id,
      orgId: fetchedAssessment.businessMeeting.orgId,
      account: fetchedAssessment.businessMeeting.account
        ? {
            id: fetchedAssessment.businessMeeting.account.id,
            orgId: fetchedAssessment.businessMeeting.account.orgId,
            name: fetchedAssessment.businessMeeting.account.name,
          }
        : null,
      recordDate: fetchedAssessment.businessMeeting.recordDate,
      filePath: fetchedAssessment.businessMeeting.filePath,
      audioFilePath: fetchedAssessment.businessMeeting.audioFilePath ?? null,
      title: fetchedAssessment.businessMeeting.title,
      transcript: fetchedAssessment.businessMeeting.transcript,
      summary: fetchedAssessment.businessMeeting.summary || null,
      summaryStatus:
        fetchedAssessment.businessMeeting.summaryStatus ?? 'COMPLETED',
      applicablePhaseNames:
        fetchedAssessment.businessMeeting.applicablePhaseNames,
      reference: fetchedAssessment.businessMeeting.reference,
    },
    assessDate: new Date(fetchedAssessment.assessDate),
    keyActionEvaluations: fetchedAssessment.keyActionEvaluations.map(
      (keyActionEvaluation) => ({
        id: keyActionEvaluation.id,
        orgId: keyActionEvaluation.orgId,
        keyAction: {
          id: keyActionEvaluation.keyAction.id,
          orgId: keyActionEvaluation.keyAction.orgId,
          name: keyActionEvaluation.keyAction.name,
          categories: keyActionEvaluation.keyAction.categories || null,
          alias:
            'KA' +
            String(keyActionEvaluation.keyAction.orderNo + 1).padStart(2, '0'),
          orderNo: keyActionEvaluation.keyAction.orderNo,
          assessmentQuestionType:
            keyActionEvaluation.keyAction.assessmentQuestionType,
        },
        score: keyActionEvaluation.score,
        evaluation: keyActionEvaluation.evaluation,
        highlight: keyActionEvaluation.highlight,
        recommendedContents: keyActionEvaluation.recommendedContents,
        roleModelAssessments: keyActionEvaluation.roleModelAssessments
          ? keyActionEvaluation.roleModelAssessments.map((rm) => ({
              id: rm.id,
              user: {
                id: rm.user.id,
                name: rm.user.name,
              },
            }))
          : [],
        timeSeries: keyActionEvaluation.timeSeriesEvaluations.map((ts) => {
          return {
            fromTime: ts.fromTime,
            toTime: ts.toTime,
            score: ts.score,
            reason: ts.reason,
            improvement: ts.improvement,
            strength: ts.strength,
            quote: ts.quote,
          }
        }),
      }),
    ),
    status: fetchedAssessment.status,
    skillMapId: fetchedAssessment.skillMapId,
    skillMapName: fetchedAssessment.skillMapName,
    durationSeconds: fetchedAssessment.durationSeconds,
    overallScore: fetchedAssessment.overallScore,
    overallScoreLogicType: fetchedAssessment.overallScoreLogicType,
    denominator: fetchedAssessment.denominator ?? null,
    overallGrade: fetchedAssessment.overallGrade ?? null,
    skipEvaluation: fetchedAssessment.skipEvaluation,
    tags: mappingAssessmentTags(fetchedAssessment.tags),
  }
}

export const mappingAssessmentSummary = (
  fetchedAssessment: FetchedAssessmentSummary,
): AssessmentSummary => {
  return {
    id: fetchedAssessment.id,
    orgId: fetchedAssessment.orgId,
    user: {
      id: fetchedAssessment.user.id,
      orgId: fetchedAssessment.user.orgId,
      name: fetchedAssessment.user.name,
      email: fetchedAssessment.user.email,
      departmentName: fetchedAssessment.user.departmentName,
      reportTo: fetchedAssessment.user.reportTo,
    },
    businessMeeting: {
      id: fetchedAssessment.businessMeeting.id,
      orgId: fetchedAssessment.businessMeeting.orgId,
      account: fetchedAssessment.businessMeeting.account
        ? {
            id: fetchedAssessment.businessMeeting.account.id,
            orgId: fetchedAssessment.businessMeeting.account.orgId,
            name: fetchedAssessment.businessMeeting.account.name,
          }
        : null,
      recordDate: fetchedAssessment.businessMeeting.recordDate,
      title: fetchedAssessment.businessMeeting.title,
      applicablePhaseNames:
        fetchedAssessment.businessMeeting.applicablePhaseNames,
    },
    assessDate: new Date(fetchedAssessment.assessDate),
    keyActionEvaluations: fetchedAssessment.keyActionEvaluations.map(
      (keyActionEvaluation) => ({
        id: keyActionEvaluation.id,
        orgId: keyActionEvaluation.orgId,
        score: keyActionEvaluation.score,
        recommendedContents: keyActionEvaluation.recommendedContents,
      }),
    ),
    status: fetchedAssessment.status,
    skillMapName: fetchedAssessment.skillMapName,
    overallScore: fetchedAssessment.overallScore,
    overallScoreLogicType: fetchedAssessment.overallScoreLogicType,
    overallGrade: fetchedAssessment.overallGrade ?? null,
    skipEvaluation: fetchedAssessment.skipEvaluation,
    tags: mappingAssessmentTags(fetchedAssessment.tags),
  }
}

export const mappingSkillMap = (skillMap: FetchedSkillMap): SkillMap => {
  return {
    defaultTargets: skillMap.defaultTargets,
    id: skillMap.id,
    name: skillMap.name,
    createdAt:
      skillMap.createdAt instanceof Date
        ? skillMap.createdAt
        : new Date(skillMap.createdAt),
    phases: skillMap.phases.map((phase) => {
      return {
        id: phase.id,
        name: phase.name,
        keyActions: phase.keyActions.map((keyAction) => {
          return {
            id: keyAction.id,
            name: keyAction.name,
            orderNo: keyAction.orderNo,
            alias: 'KA' + String(keyAction.orderNo + 1).padStart(2, '0'),
          }
        }),
      }
    }),
  }
}

interface TranscriptContentModel {
  assessment?: string | null
  reason?: string | null
  reasonDetail?: string | null
}

interface TranscriptEntryModel {
  type?: string | null
  speaker: string
  speakedAt: string
  quote: string
  content?: TranscriptContentModel | null
}

export interface FetchedAssessment {
  id: string
  orgId: string
  user: {
    id: string
    orgId: string
    name: string
    email: string
    departmentName?: string | null
  }
  businessMeeting: {
    id: string
    orgId: string
    account?: {
      id: string
      orgId: string
      name: string
    } | null
    recordDate: Date
    filePath: string
    audioFilePath?: string | null
    title: string
    transcript?: TranscriptEntryModel[]
    summary?: string | null
    summaryStatus?: string
    applicablePhaseNames?: string[]
    reference?: string | null
  }
  skillMapId: string
  skillMapName?: string
  assessDate: Date
  keyActionEvaluations: FetchedKeyActionEvaluation[]
  status: AssessmentStatus
  durationSeconds: number
  overallScore: number
  overallScoreLogicType: OverallScoreLogicType
  denominator?: number | null
  overallGrade?: string | null
  skipEvaluation: boolean
  tags: FetchedAssessmentTag[]
}

export interface FetchedAssessmentSummary {
  id: string
  orgId: string
  user: {
    id: string
    orgId: string
    name: string
    email: string
    departmentName?: string | null
    reportTo?: {
      id: string
    } | null
  }
  businessMeeting: {
    id: string
    orgId: string
    account?: {
      id: string
      orgId: string
      name: string
    } | null
    recordDate: Date
    title: string
    applicablePhaseNames?: string[]
  }
  skillMapName?: string
  assessDate: Date
  keyActionEvaluations: {
    id: string
    orgId: string
    score: number | null
    recommendedContents: RecommendedContent[]
  }[]
  status: AssessmentStatus
  overallScore: number | null
  overallScoreLogicType: OverallScoreLogicType
  overallGrade?: string | null
  skipEvaluation: boolean
  tags: FetchedAssessmentTag[]
}

interface FetchedSkillMap {
  defaultTargets: DefaultSkillMapTarget[]
  id: string
  name: string
  createdAt: Date | string
  phases: FetchedPhase[]
}

interface FetchedPhase {
  id: string
  name: string
  keyActions: FetchedKeyAction[]
}

interface FetchedKeyAction {
  id: string
  name: string
  orderNo: number
}

export interface Assessment {
  id: string
  orgId: string
  user: {
    id: string
    orgId: string
    name: string
    email: string
    departmentName?: string | null
  }
  businessMeeting: {
    id: string
    orgId: string
    account?: {
      id: string
      orgId: string
      name: string
    } | null
    recordDate: Date
    filePath: string
    audioFilePath: string | null
    title: string
    transcript?: TranscriptEntryModel[]
    summary: string | null
    summaryStatus: string
    applicablePhaseNames?: string[]
    reference?: string | null
  }
  assessDate: Date
  keyActionEvaluations: KeyActionEvaluation[]
  status: AssessmentStatus
  skillMapId: string
  skillMapName?: string
  durationSeconds: number
  overallScore: number
  overallScoreLogicType: OverallScoreLogicType
  denominator?: number | null
  overallGrade?: string | null
  skipEvaluation: boolean
  tags: AssessmentTag[]
}

export interface AssessmentSummary {
  id: string
  orgId: string
  user: {
    id: string
    orgId: string
    name: string
    email: string
    departmentName?: string | null
    reportTo?: {
      id: string
    } | null
  }
  businessMeeting: {
    id: string
    orgId: string
    account?: {
      id: string
      orgId: string
      name: string
    } | null
    recordDate: Date
    title: string
    applicablePhaseNames?: string[]
  }
  assessDate: Date
  keyActionEvaluations: {
    id: string
    orgId: string
    score: number | null
    recommendedContents: RecommendedContent[]
  }[]
  status: AssessmentStatus
  skillMapName?: string
  overallScore: number | null
  overallScoreLogicType: OverallScoreLogicType
  overallGrade?: string | null
  skipEvaluation: boolean
  tags: AssessmentTag[]
}

export interface SkillMap {
  defaultTargets: DefaultSkillMapTarget[]
  id: string
  name: string
  createdAt: Date
  phases: Phase[]
}

export interface Phase {
  id: string
  name: string
  keyActions: KeyAction[]
}

export interface KeyAction {
  id: string
  name: string
  orderNo: number
  alias: string
}

export interface RecommendedContent {
  id: string
  content: Content
  isAccessed: boolean
}

export interface Content {
  name: string
  url: string
}

export interface RoleModelAssessment {
  id: string
  user: {
    id: string
    name: string
  }
}

export interface KeyActionEvaluation {
  id: string
  orgId: string
  keyAction: {
    id: string
    orgId: string
    name: string
    categories: string[] | null
    orderNo: number
    alias: string
    assessmentQuestionType: AssessmentQuestionType
  }
  score: number
  evaluation: string
  highlight: string
  recommendedContents: RecommendedContent[]
  roleModelAssessments: RoleModelAssessment[]
  timeSeries: TimeSeriesEvaluation[]
}

export interface FetchedRoleModelAssessment {
  id: string
  user: {
    id: string
    name: string
  }
}

interface FetchedKeyActionEvaluation {
  id: string
  orgId: string
  keyAction: {
    id: string
    orgId: string
    name: string
    categories?: string[] | null
    orderNo: number
    assessmentQuestionType: AssessmentQuestionType
  }
  score: number
  evaluation: string
  highlight: string
  recommendedContents: RecommendedContent[]
  roleModelAssessments: FetchedRoleModelAssessment[]
  timeSeriesEvaluations: FetchedKeyActionTimeSeriesEvaluation[]
}

export interface TimeSeriesEvaluation {
  fromTime: string
  toTime: string
  score: number
  reason: string
  improvement: string
  strength: string
  quote: string
}

interface FetchedKeyActionTimeSeriesEvaluation {
  fromTime: string
  toTime: string
  score: number
  reason: string
  improvement: string
  strength: string
  quote: string
}
